"use client";

import { useMemo } from "react";

interface PasswordStrengthBarProps {
  password?: string;
  className?: string;
  minLength?: number;
}

type StrengthLevel = 0 | 1 | 2 | 3 | 4;

interface StrengthResult {
  score: StrengthLevel;
  label: string;
  color: string;
}

function calculateStrength(password: string, minLength: number): StrengthResult {
  if (!password || password.length === 0) {
    return { score: 0, label: "", color: "#ddd" };
  }

  // Too short - always show as weak
  if (password.length < minLength) {
    return { score: 1, label: "Too short", color: "#ef4444" };
  }

  let score = 1; // Start at 1 (Weak) once minLength is met

  // Length bonuses - more generous
  if (password.length >= minLength + 2) score++;
  if (password.length >= minLength + 6) score++;

  // Character variety - any one of these bumps the score
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  // Cap at 4
  const normalizedScore = Math.min(4, score) as StrengthLevel;

  const results: Record<StrengthLevel, StrengthResult> = {
    0: { score: 0, label: "Too short", color: "#ddd" },
    1: { score: 1, label: "Weak", color: "#ef4444" },
    2: { score: 2, label: "Fair", color: "#f97316" },
    3: { score: 3, label: "Good", color: "#eab308" },
    4: { score: 4, label: "Strong", color: "#22c55e" },
  };

  return results[normalizedScore];
}

export default function PasswordStrengthBar({
  password = "",
  className = "",
  minLength = 8,
}: PasswordStrengthBarProps) {
  const strength = useMemo(() => calculateStrength(password, minLength), [password, minLength]);

  if (!password) return null;

  return (
    <div className={className}>
      <div className="flex gap-1 h-1">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className="flex-1 rounded-sm transition-colors duration-200"
            style={{
              backgroundColor: strength.score >= level ? strength.color : "#e5e7eb",
            }}
          />
        ))}
      </div>
      {strength.label && (
        <p
          className="text-xs mt-1 text-right transition-colors duration-200"
          style={{ color: strength.color }}
        >
          {strength.label}
        </p>
      )}
    </div>
  );
}
